/**
 * Display formatting for the LCFS allocation figures.
 *
 * Extracted when the drill-down drawers became the third and fourth screen to
 * need it — `AllocationLedger` and `LotInventory` each carried their own copy,
 * and four would have been four chances for the ledger and its own drill-down to
 * render the same figure differently.
 *
 * `toNumber()` appears here and nowhere else in the allocation path. It is not a
 * violation of "never `Number()` a figure": the value has already been rounded
 * to its display precision by the Decimal above it, and what `toLocaleString`
 * adds is thousands separators. No arithmetic happens after this point, and the
 * figure that reaches a filing never passes through here.
 */

import { Decimal } from "@compliance/domain";

/** Thousands-separated at `dp` decimals. Falls back to the raw string. */
export function fmtMmbtu(value: string, dp = 3): string {
  try {
    return new Decimal(value).toDecimalPlaces(dp).toNumber().toLocaleString("en-US", {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    });
  } catch {
    return value;
  }
}

/**
 * Below half of the last displayed decimal — i.e. renders as zero.
 *
 * Used to decide whether a residual is worth a badge. A figure that rounds to
 * `0.000` but is not exactly zero is dust from a merge split, not a finding.
 */
export function isZeroMmbtu(value: string): boolean {
  try {
    return new Decimal(value).abs().lte("0.005");
  } catch {
    return false;
  }
}
